import React, { useMemo } from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { keyframes } from '@mui/material/styles';
import { flowerForCriterion } from './criterionFlower';

const PASTELS = ['#e8f5e9', '#e3f2fd', '#fff3e0', '#f3e5f5', '#fffde7', '#e0f7fa', '#fce4ec'];
const EMOJI_AVATARS = ['🐰', '🦊', '🐻', '🐼', '🐨', '🦁', '🐯', '🐸', '🐧', '🦉', '🐝', '🦋'];
const flowerBloom = keyframes`from { transform: scale(0.94); } to { transform: scale(1); }`;

export default function StudentCard({
  student, progress = {}, flowerCriteria = [], animateStage = false, compact = false,
  selected = false, selectable = false, awardedToday = false, onClick, sx = {},
}) {
  const visuals = useMemo(() => {
    let hash = 0;
    const id = String(student.id);
    for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) | 0;
    return { background: PASTELS[Math.abs(hash) % PASTELS.length], avatar: EMOJI_AVATARS[Math.abs(hash) % EMOJI_AVATARS.length] };
  }, [student.id]);
  const counts = progress.criteriaCounts || {};

  return (
    <Paper elevation={4} onClick={awardedToday ? undefined : onClick} aria-disabled={awardedToday || undefined} sx={{
      bgcolor: visuals.background, borderRadius: 4, p: compact ? { xs: 2, md: 2.5 } : 3,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: compact ? 1.5 : 2,
      boxSizing: 'border-box', width: '100%', maxWidth: compact ? 'none' : 480, minWidth: 0, minHeight: compact ? 300 : 420,
      userSelect: 'none', border: selected ? '3px solid #2e7d32' : '3px solid transparent',
      cursor: selectable && !awardedToday ? 'pointer' : 'default',
      '&:hover': selectable && !awardedToday ? { transform: 'translateY(-2px)' } : undefined,
      ...sx,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar sx={{ bgcolor: '#fff', width: compact ? 44 : 48, height: compact ? 44 : 48, fontSize: compact ? 25 : 28 }}>
          {visuals.avatar}
        </Avatar>
        <Typography variant="h5" sx={{ fontWeight: 800, fontSize: compact ? 22 : 27, overflowWrap: 'anywhere' }}>{student.firstName}</Typography>
      </Box>
      <Typography sx={{ color: '#365314', fontWeight: 800, fontSize: compact ? 16 : undefined }}>Yıldız Bahçem · ⭐ {Number(progress.lifetimeStars || 0)}</Typography>
      {awardedToday && <Typography sx={{ bgcolor: '#dcedc8', color: '#33691e', borderRadius: 2, px: 1.5, py: 0.75, fontWeight: 800 }}>Bugün puan verildi ✓</Typography>}
      <Box sx={{ display: 'grid', gridTemplateColumns: compact ? 'minmax(0, 1fr)' : { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }, gap: compact ? 1.1 : 1,
        width: '100%', minWidth: 0,
        animation: animateStage ? `${flowerBloom} 500ms ease-out` : undefined }}>
        {flowerCriteria.map((criterion) => (
          <Box key={criterion.id} sx={{ bgcolor: 'rgba(255,255,255,0.82)', borderRadius: 2, p: compact ? 1.25 : 1.1,
            minWidth: 0, minHeight: compact ? 58 : undefined, display: 'flex', alignItems: 'center', gap: compact ? 1.1 : 0.8 }}>
            <Box aria-hidden="true" sx={{ fontSize: compact ? 30 : 36, lineHeight: 1, flex: '0 0 auto' }}>{flowerForCriterion(flowerCriteria, criterion.id)}</Box>
            <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: compact ? 'row' : 'column', alignItems: compact ? 'center' : 'flex-start', gap: compact ? 1 : 0 }}>
              <Typography sx={{ minWidth: 0, flex: compact ? 1 : undefined, fontSize: compact ? 15 : 14, fontWeight: 700, lineHeight: 1.3, overflowWrap: 'break-word' }}>
                {criterion.name}
              </Typography>
              <Typography sx={{ flex: '0 0 auto', whiteSpace: compact ? 'nowrap' : undefined, fontSize: compact ? 16 : 17, fontWeight: 900, color: '#92400e' }}>
                ⭐ {Number(counts[String(criterion.id)] || 0)}
              </Typography>
            </Box>
          </Box>
        ))}
        {flowerCriteria.length === 0 && <Typography sx={{ gridColumn: '1 / -1', textAlign: 'center' }}>Henüz çiçek yok.</Typography>}
      </Box>
    </Paper>
  );
}
